import { getMatchingAppLocaleFromResponse } from '@/src/lib/dictionary/settings';
import { getDictionary } from '@/src/lib/dictionary/dictionaries';
import { GlueErrorResponse } from '@/src/lib/glue/types';
import {
  ERROR_CODE_TRANSLATION_MAP,
  ERROR_CODES_TO_BE_MASKED,
  RESPONSE_MESSAGE_FETCH_FAILED,
} from '@/src/lib/utils/constants';
import { transformExceptionToErrorObject } from '@/src/lib/utils/error';
import { isGlueErrorResponse } from '@/src/lib/glue/business/is-glue-error';
import { captureException } from '@sentry/core';
import { GlueApiError } from '@/src/lib/http/business/http-error';
import { Dictionary } from '@/src/lib/dictionary/types';

async function getDictionaryForResponseLang(response: Response): Promise<Dictionary> {
  const lang = getMatchingAppLocaleFromResponse(response);
  return await getDictionary(lang);
}

function glueResponseContainsMaskableErrors(response: GlueErrorResponse) {
  return response.errors.some(
    (error) => ERROR_CODES_TO_BE_MASKED.includes(error.code) || error.detail === RESPONSE_MESSAGE_FETCH_FAILED,
  );
}

function glueResponseContainsLocalizableErrors(response: GlueErrorResponse) {
  return response.errors.some((error) => ERROR_CODE_TRANSLATION_MAP.has(error.code));
}

function maskGlueErrors(response: GlueErrorResponse, maskedMessage: string) {
  return response.errors.map((error) => {
    return {
      ...error,
      detail: maskedMessage,
    };
  });
}

export async function handleResponseError(response: Response): Promise<never> {
  const errorMessage = await response.text();
  const error = transformExceptionToErrorObject(errorMessage);

  if (isGlueErrorResponse(error)) {
    if (glueResponseContainsMaskableErrors(error)) {
      captureException(error);
      const dict = await getDictionaryForResponseLang(response);

      throw new GlueApiError(dict.general.error, response.status, maskGlueErrors(error, dict.general.error));
    }

    if (glueResponseContainsLocalizableErrors(error)) {
      captureException(error);
      const dict = await getDictionaryForResponseLang(response);
      const translationKey = ERROR_CODE_TRANSLATION_MAP.get(error.errors[0].code) as
        | keyof typeof dict.errorCodes
        | undefined;

      if (!translationKey || !(translationKey in dict.errorCodes)) {
        throw new GlueApiError(dict.general.error, response.status, error.errors);
      }

      // eslint-disable-next-line security/detect-object-injection
      const newErrorMessage = dict.errorCodes[translationKey];

      throw new GlueApiError(newErrorMessage, response.status, maskGlueErrors(error, newErrorMessage));
    }

    const errorMessage = error.errors.map((e) => e.detail).join(', ');
    throw new GlueApiError(errorMessage, response.status, error.errors);
  }
  throw new Error(JSON.stringify(error));
}
